/**
 * Admin Management — `/platform/admin-management`, Main Super Admin only.
 *
 * Two tabs: the platform admins (edit, activate/deactivate, delete, make or
 * remove Main Super Admin) and the registration tokens new admins register
 * with. The backend enforces every rule (Main-only, last Main can't be removed,
 * no self-delete); this page only reflects them.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
  Ban,
  Copy,
  KeyRound,
  Pencil,
  Power,
  PowerOff,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  Trash2,
  Users,
} from 'lucide-react';
import { Button, Card, Spinner, useToast } from '@centraoneplus/ui';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { useAuthStore } from '@stores/authStore';
import {
  platformAdminManagementService as api,
  type CreatedPlatformInviteToken,
  type PlatformAdmin,
  type PlatformAdminAction,
  type PlatformInviteToken,
} from '@services/platform-admin-management.service';

type Tab = 'admins' | 'tokens';

function errorMessage(err: unknown, fallback: string): string {
  const data = (err as AxiosError<{ message?: string | string[]; error?: { message?: string } }>).response
    ?.data;
  const message = data?.error?.message || data?.message;
  return Array.isArray(message) ? message.join(', ') : message || fallback;
}

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '—');
const adminName = (a: PlatformAdmin) =>
  a.displayName || [a.firstName, a.lastName].filter(Boolean).join(' ') || '—';

// Both tables use a fixed layout: every column but the first has a set width
// and the first takes the rest, truncating long names/emails instead of
// pushing the table past its card. Narrower than min-w, the card scrolls.
const TABLE = 'w-full table-fixed text-sm';
const TH = 'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap';
const TD = 'px-4 py-3 align-middle';

/** Date on one line, time underneath; '—' when there is none. */
function DateCell({ value, note }: { value?: string | null; note?: string | null }) {
  if (!value) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  const d = new Date(value);
  return (
    <div className="whitespace-nowrap" title={d.toLocaleString()}>
      <div className="text-gray-700 dark:text-gray-300">
        {d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
        {note ?? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
}

/** Icon-only row action; the label is the tooltip and the accessible name. */
function IconAction({
  label,
  icon: Icon,
  onClick,
  disabled,
  danger,
}: {
  label: string;
  icon: typeof Pencil;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-md text-gray-500 dark:text-gray-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        danger
          ? 'hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400'
          : 'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white'
      }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

export default function PlatformAdminManagementPage() {
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<Tab>('admins');

  if (!user?.isPrimary) {
    return (
      <Card className="p-6 text-sm text-gray-600 dark:text-gray-300 text-center">
        Only the Main Super Admin can manage platform admins.
      </Card>
    );
  }

  const tabClass = (t: Tab) =>
    `flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
      tab === t
        ? 'border-indigo-600 text-indigo-700 dark:text-indigo-300'
        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
    }`;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Admin Management</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Manage platform admins and the registration tokens new admins sign up with.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700" role="tablist">
        <button type="button" role="tab" className={tabClass('admins')} onClick={() => setTab('admins')}>
          <Users className="w-4 h-4" /> Admins
        </button>
        <button type="button" role="tab" className={tabClass('tokens')} onClick={() => setTab('tokens')}>
          <KeyRound className="w-4 h-4" /> Registration Tokens
        </button>
      </div>

      {tab === 'admins' ? <AdminsTab currentUserId={user.id} /> : <TokensTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function AdminsTab({ currentUserId }: { currentUserId: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const updateUser = useAuthStore((s) => s.updateUser);
  const [admins, setAdmins] = useState<PlatformAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<PlatformAdmin | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      setAdmins(await api.listAdmins());
    } catch (err) {
      setError(errorMessage(err, 'Could not load platform admins.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const run = async (
    admin: PlatformAdmin,
    label: string,
    call: () => Promise<void>,
    afterSuccess: () => Promise<void> | void = refresh,
  ) => {
    setBusyId(admin.id);
    try {
      await call();
      toast.success(label, admin.email);
      await afterSuccess();
    } catch (err) {
      toast.error('Action failed', errorMessage(err, 'Please try again.'));
    } finally {
      setBusyId(null);
    }
  };

  const action = (admin: PlatformAdmin, act: PlatformAdminAction, confirmText: string, label: string) => {
    // Removing your own Main rights ends your access to this page. Drop the
    // flag from the signed-in user (hides the nav item) and leave, rather
    // than reload a list the backend now refuses with 403.
    if (act === 'demote' && admin.id === currentUserId) {
      const ownConfirm =
        'Remove your own Main Super Admin rights? You will lose access to Admin Management straight away.';
      if (!window.confirm(ownConfirm)) return;
      run(admin, label, () => api.adminAction(admin.id, act), () => {
        updateUser({ isPrimary: false });
        navigate('/platform/tenants', { replace: true });
      });
      return;
    }
    if (!window.confirm(confirmText)) return;
    run(admin, label, () => api.adminAction(admin.id, act));
  };

  const remove = (admin: PlatformAdmin) => {
    if (!window.confirm(`Delete ${admin.email}? They will lose access to the platform immediately.`)) return;
    run(admin, 'Admin deleted', () => api.deleteAdmin(admin.id));
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      {error && <Card className="p-4 bg-red-50 text-red-800 text-sm">{error}</Card>}

      <Card className="overflow-x-auto">
        <table className={`${TABLE} min-w-[860px]`}>
          <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            <tr>
              <th className={TH}>Admin</th>
              <th className={`${TH} w-44`}>Role</th>
              <th className={`${TH} w-24`}>Status</th>
              <th className={`${TH} w-32`}>Created</th>
              <th className={`${TH} w-32`}>Last login</th>
              <th className={`${TH} w-36 text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => {
              const isSelf = a.id === currentUserId;
              const busy = busyId === a.id;
              return (
                <tr
                  key={a.id}
                  className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50/60 dark:hover:bg-gray-800/40"
                >
                  <td className={TD}>
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-1.5 min-w-0">
                        <span className="font-medium text-gray-900 dark:text-white truncate" title={adminName(a)}>
                          {adminName(a)}
                        </span>
                        {isSelf && <span className="flex-none text-xs text-gray-500">(you)</span>}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate" title={a.email}>
                        {a.email}
                      </div>
                    </div>
                  </td>
                  <td className={TD}>
                    {a.isPrimary ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">
                        <ShieldCheck className="w-3 h-3 flex-none" /> Main Super Admin
                      </span>
                    ) : (
                      <span className="whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {a.platformRole === 'SUPER_ADMIN' ? 'Platform Admin' : a.platformRole}
                      </span>
                    )}
                  </td>
                  <td className={TD}>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                        a.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className={TD}>
                    <DateCell value={a.createdAt} />
                  </td>
                  <td className={TD}>
                    <DateCell value={a.lastLoginAt} />
                  </td>
                  <td className={TD}>
                    <div className="flex justify-end gap-0.5">
                      <IconAction label={`Edit ${a.email}`} icon={Pencil} disabled={busy} onClick={() => setEditing(a)} />
                      {a.isPrimary ? (
                        <IconAction
                          label="Remove Main Super Admin rights"
                          icon={ShieldOff}
                          disabled={busy}
                          onClick={() =>
                            action(
                              a,
                              'demote',
                              `Remove Main Super Admin rights from ${a.email}? They stay a Platform Admin.`,
                              'Main Super Admin rights removed',
                            )
                          }
                        />
                      ) : (
                        a.platformRole === 'SUPER_ADMIN' &&
                        a.status === 'active' && (
                          <IconAction
                            label="Make Main Super Admin"
                            icon={ShieldCheck}
                            disabled={busy}
                            onClick={() =>
                              action(
                                a,
                                'promote',
                                `Make ${a.email} a Main Super Admin? They will be able to manage all platform admins and registration tokens.`,
                                'Now a Main Super Admin',
                              )
                            }
                          />
                        )
                      )}
                      {!isSelf &&
                        (a.status === 'active' ? (
                          <IconAction
                            label="Deactivate"
                            icon={PowerOff}
                            disabled={busy}
                            onClick={() =>
                              action(
                                a,
                                'deactivate',
                                `Deactivate ${a.email}? They won't be able to sign in until reactivated.`,
                                'Admin deactivated',
                              )
                            }
                          />
                        ) : (
                          <IconAction
                            label="Activate"
                            icon={Power}
                            disabled={busy}
                            onClick={() => action(a, 'activate', `Reactivate ${a.email}?`, 'Admin activated')}
                          />
                        ))}
                      {!isSelf && (
                        <IconAction label="Delete" icon={Trash2} danger disabled={busy} onClick={() => remove(a)} />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {editing && (
        <EditAdminPanel
          admin={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function EditAdminPanel({
  admin,
  onClose,
  onSaved,
}: {
  admin: PlatformAdmin;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [firstName, setFirstName] = useState(admin.firstName || '');
  const [lastName, setLastName] = useState(admin.lastName || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter a first and last name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.updateAdmin(admin.id, { firstName: firstName.trim(), lastName: lastName.trim() });
      toast.success('Admin updated', admin.email);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the changes.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4">
      <form className="space-y-4" onSubmit={save}>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Edit {admin.email}</h2>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="edit-first" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              First name
            </label>
            <input
              id="edit-first"
              className="input mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="edit-last" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Last name
            </label>
            <input
              id="edit-last"
              className="input mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<PlatformInviteToken['status'], string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  used: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  revoked: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  expired: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
};

function TokensTab() {
  const toast = useToast();
  const [tokens, setTokens] = useState<PlatformInviteToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [hours, setHours] = useState('72');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedPlatformInviteToken | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const portalUrl = `${window.location.origin}/platform-admin`;

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      setTokens(await api.listTokens());
    } catch (err) {
      setError(errorMessage(err, 'Could not load registration tokens.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      setCreated(await api.createToken(email.trim(), Number(hours)));
      setEmail('');
      await refresh();
    } catch (err) {
      toast.error('Could not generate token', errorMessage(err, 'Please try again.'));
    } finally {
      setCreating(false);
    }
  };

  const run = async (t: PlatformInviteToken, call: () => Promise<unknown>, label: string) => {
    setBusyId(t.id);
    try {
      const result = await call();
      if (result && typeof result === 'object' && 'token' in result) {
        setCreated(result as CreatedPlatformInviteToken);
      }
      toast.success(label, t.email);
      await refresh();
    } catch (err) {
      toast.error('Action failed', errorMessage(err, 'Please try again.'));
    } finally {
      setBusyId(null);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied', 'Copied to the clipboard');
    } catch {
      toast.error('Copy failed', 'Select the text and copy it manually.');
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <form className="flex flex-col sm:flex-row sm:items-end gap-3" onSubmit={generate}>
          <div className="flex-1">
            <label htmlFor="token-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              New admin's email
            </label>
            <input
              id="token-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              placeholder="new.admin@company.com"
            />
          </div>
          <div className="sm:w-44">
            <label htmlFor="token-expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Expires after
            </label>
            <ThemedSelect
              id="token-expiry"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="input mt-1"
            >
              <option value="24">24 hours</option>
              <option value="72">3 days</option>
              <option value="168">7 days</option>
            </ThemedSelect>
          </div>
          <Button type="submit" disabled={creating}>
            {creating ? 'Generating…' : 'Generate Token'}
          </Button>
        </form>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          The token works once, only for this email, until it expires. Generating a new token for the same
          email deactivates the previous one.
        </p>
      </Card>

      {created && (
        <Card className="p-4 border-2 border-indigo-300 dark:border-indigo-700 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Token for {created.email}
              </h2>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Copy it now — it will not be shown again. Expires {formatDate(created.expiresAt)}.
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setCreated(null)}>
              Dismiss
            </Button>
          </div>
          <CopyRow label="Registration token" value={created.token} onCopy={copy} />
          <CopyRow label="Admin portal URL" value={portalUrl} onCopy={copy} />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Share the token and the URL with the new admin through separate private channels.
          </p>
        </Card>
      )}

      {error && <Card className="p-4 bg-red-50 text-red-800 text-sm">{error}</Card>}

      {loading ? (
        <Spinner />
      ) : tokens.length === 0 ? (
        <Card className="p-6 text-sm text-gray-500 text-center">No registration tokens yet.</Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className={`${TABLE} min-w-[820px]`}>
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              <tr>
                <th className={TH}>Invitee</th>
                <th className={`${TH} w-28`}>Status</th>
                <th className={`${TH} w-36`}>Created</th>
                <th className={`${TH} w-32`}>Expires</th>
                <th className={`${TH} w-36`}>Used by</th>
                <th className={`${TH} w-28 text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => {
                const busy = busyId === t.id;
                const createdBy = t.createdBy?.displayName || t.createdBy?.email;
                const usedBy = t.usedBy?.displayName || t.usedBy?.email;
                return (
                  <tr
                    key={t.id}
                    className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50/60 dark:hover:bg-gray-800/40"
                  >
                    <td className={TD}>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white truncate" title={t.email}>
                          {t.email}
                        </div>
                        <div className="font-mono text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {t.tokenHint}
                        </div>
                      </div>
                    </td>
                    <td className={TD}>
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${STATUS_STYLES[t.status]}`}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td className={TD}>
                      <DateCell value={t.createdAt} note={createdBy ? `by ${createdBy}` : null} />
                    </td>
                    <td className={TD}>
                      <DateCell value={t.expiresAt} />
                    </td>
                    <td className={TD}>
                      {usedBy && t.usedAt ? (
                        <DateCell value={t.usedAt} note={usedBy} />
                      ) : usedBy ? (
                        <div className="text-gray-700 dark:text-gray-300 truncate" title={usedBy}>
                          {usedBy}
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className={TD}>
                      <div className="flex justify-end gap-0.5">
                        {t.status === 'active' && (
                          <IconAction
                            label="Deactivate token"
                            icon={Ban}
                            disabled={busy}
                            onClick={() => run(t, () => api.revokeToken(t.id), 'Token deactivated')}
                          />
                        )}
                        {t.status !== 'used' && (
                          <IconAction
                            label="Regenerate token"
                            icon={RefreshCw}
                            disabled={busy}
                            onClick={() => run(t, () => api.regenerateToken(t.id), 'New token generated')}
                          />
                        )}
                        <IconAction
                          label="Delete token"
                          icon={Trash2}
                          danger
                          disabled={busy}
                          onClick={() => {
                            if (window.confirm(`Delete the token for ${t.email}? It can never be used again.`)) {
                              run(t, () => api.deleteToken(t.id), 'Token deleted');
                            }
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function CopyRow({ label, value, onCopy }: { label: string; value: string; onCopy: (v: string) => void }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{label}</p>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
          {value}
        </code>
        <Button size="sm" variant="outline" onClick={() => onCopy(value)} aria-label={`Copy ${label}`}>
          <Copy className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
